import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  "aria-label": "breadcrumb",
  class: "mt-5 pt-5 ps-3"
}
const _hoisted_2 = { class: "breadcrumb mb-3" }
const _hoisted_3 = { class: "breadcrumb-item" }
const _hoisted_4 = { class: "breadcrumb-item" }
const _hoisted_5 = {
  class: "breadcrumb-item active",
  "aria-current": "page"
}
const _hoisted_6 = { class: "container-fluid" }
const _hoisted_7 = { class: "row" }
const _hoisted_8 = { class: "col-lg-8 col-md-10 mx-auto" }
const _hoisted_9 = { class: "card" }
const _hoisted_10 = { class: "card-header" }
const _hoisted_11 = { class: "mb-1 fw-bold" }
const _hoisted_12 = { class: "text-muted" }
const _hoisted_13 = { class: "card-body" }
const _hoisted_14 = {
  key: 0,
  class: "text-center py-4"
}
const _hoisted_15 = { class: "mb-3" }
const _hoisted_16 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_17 = { class: "mb-3" }
const _hoisted_18 = ["value"]
const _hoisted_19 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_20 = { class: "mb-3" }
const _hoisted_21 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_22 = { class: "mb-3" }
const _hoisted_23 = { class: "form-check form-switch px-0" }
const _hoisted_24 = {
  key: 0,
  class: "alert alert-danger",
  role: "alert"
}
const _hoisted_25 = { class: "d-flex gap-2 justify-content-end" }
const _hoisted_26 = ["disabled"]
const _hoisted_27 = { key: 0 }
const _hoisted_28 = { key: 1 }

import { ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";

// Router
type MetaKey =
  | "capabilities"
  | "roles"
  | "competencies"
  | "rules"
  | "skills"
  | "levels";

// Reactive data

export default /*@__PURE__*/_defineComponent({
  __name: 'KnowledgeBaseForm',
  setup(__props) {

const router = useRouter();
const route = useRoute();

// Computed
const isEditMode = computed(() => !!route.params.id);
const knowledgeBaseId = computed(() => route.params.id as string);

const formData = reactive({
  name: "",
  description: "",
  category: "",
  isActive: true, 
});
const kbCategories = ref<any[]>([]);
const loadCategories = async () => {
  try {
    const response = await apiService.get("/knowledge-base-categories");

    // Adjust depending on API response structure
    kbCategories.value = response?.data?.map((cat: any) => ({
      label: cat.name,
      value: cat.slug || cat._id || cat.name,
    })) || [];

  } catch (error) {
    console.error("Failed to load KB categories", error);
  }
};
 

const metaOptions = reactive<Record<string, any[]>>({
  capabilities: [],
  roles: [],
  competencies: [],
  rules: [],
  skills: [],
  levels: [],
});

const activeTab = ref<MetaKey>("capabilities");
const isMetaLoading = ref(false);

const errors = reactive<Record<string, string>>({});
const isLoading = ref(false);
const isSaving = ref(false);
const submitError = ref<string | null>(null);

// Methods
const loadKnowledgeBase = async () => {
  if (!isEditMode.value) return;

  isLoading.value = true;
  try {
    const response = await apiService.getKnowledgeBaseById(
      knowledgeBaseId.value
    );

    // Populate form with existing data
    if (response) {
      formData.name = response.name || "";
      formData.description = response.description || "";
      formData.category = response.category || "";
      formData.isActive = response.isActive ?? true; 
    }
  } catch (err: any) {
    console.error("Error loading knowledge base:", err);
    showToast(
      err?.response?.data?.message || "Failed to load knowledge base",
      "error"
    );
    // Redirect back if not found
    router.push("/admin/knowledge-bases");
  } finally {
    isLoading.value = false;
  }
};

 

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach((key) => delete errors[key]);

  let isValid = true;

  // Validate name
  if (!formData.name.trim()) {
    errors.name = "Name is required";
    isValid = false;
  } else if (formData.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
    isValid = false;
  }

  // Validate description (optional, but if provided, check length)
  if (formData.description && formData.description.length > 1000) {
    errors.description = "Description must not exceed 1000 characters";
    isValid = false;
  }

  // Validate category
  if (!formData.category) {
    errors.category = "Category is required";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  submitError.value = null;

  // Validate form
  if (!validateForm()) {
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      category: formData.category,
      isActive: formData.isActive, 
    };

    if (isEditMode.value) {
      // Update existing knowledge base
      await apiService.updateKnowledgeBase(knowledgeBaseId.value, payload);
      showToast("Knowledge base updated successfully", "success");
      router.push(`/admin/knowledge-bases/${knowledgeBaseId.value}`);
    } else {
      // Create new knowledge base
      const response = await apiService.createKnowledgeBase(payload);
      showToast("Knowledge base created successfully", "success");

      // Redirect to the new knowledge base details page
      if (response._id || response.id) {
        router.push(`/admin/knowledge-bases/${response._id || response.id}`);
      } else {
        router.push("/admin/knowledge-bases");
      }
    }
  } catch (err: any) {
    console.error("Error saving knowledge base:", err);
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to save knowledge base";
    submitError.value = errorMessage;
    showToast(errorMessage, "error");
  } finally {
    isSaving.value = false;
  }
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  // Simple toast implementation - you can replace with your toast library
  console.log(`[${type.toUpperCase()}] ${message}`);

  // You can integrate with a toast library like vue-toastification here
  // For now, using alert for errors only
  if (type === "error") {
    // alert(message);
  }
};

// Lifecycle
onMounted(() => {
  loadCategories(); 
  if (isEditMode.value) {
    loadKnowledgeBase();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("nav", _hoisted_1, [
      _createElementVNode("ol", _hoisted_2, [
        _createElementVNode("li", _hoisted_3, [
          _createVNode(_component_router_link, { to: "/" }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode("Home", -1)
            ]))]),
            _: 1
          })
        ]),
        _createElementVNode("li", _hoisted_4, [
          _createVNode(_component_router_link, { to: "/admin/knowledge-bases" }, {
            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
              _createTextVNode("Knowledge Bases", -1)
            ]))]),
            _: 1
          })
        ]),
        _createElementVNode("li", _hoisted_5, _toDisplayString(isEditMode.value ? "Edit" : "New"), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("h5", _hoisted_11, _toDisplayString(isEditMode.value ? "Edit Knowledge Base" : "Create Knowledge Base"), 1),
              _createElementVNode("small", _hoisted_12, _toDisplayString(isEditMode.value
                    ? "Update knowledge base details"
                    : "Create a new knowledge base for your documents"), 1)
            ]),
            _createElementVNode("div", _hoisted_13, [
              (isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("div", {
                      class: "spinner-border loading-spinner text-primary",
                      role: "status"
                    }, [
                      _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                    ], -1),
                    _createElementVNode("p", { class: "mt-2" }, "Loading knowledge base...", -1)
                  ]))]))
                : (_openBlock(), _createElementBlock("form", {
                    key: 1,
                    onSubmit: _withModifiers(handleSubmit, ["prevent"])
                  }, [
                    _createElementVNode("div", _hoisted_15, [
                      _cache[7] || (_cache[7] = _createElementVNode("label", {
                        for: "name",
                        class: "form-label fw-bold"
                      }, [
                        _createTextVNode(" Name "),
                        _createElementVNode("span", { class: "text-danger" }, "*")
                      ], -1)),
                      _withDirectives(_createElementVNode("input", {
                        type: "text",
                        class: _normalizeClass(["form-control", { 'is-invalid': errors.name }]),
                        id: "name",
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.name) = $event)),
                        placeholder: "Enter knowledge base name",
                        required: ""
                      }, null, 2), [
                        [_vModelText, formData.name]
                      ]),
                      (errors.name)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(errors.name), 1))
                        : _createCommentVNode("", true),
                      _cache[8] || (_cache[8] = _createElementVNode("small", { class: "form-text text-muted" }, " A descriptive name for your knowledge base ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      _cache[10] || (_cache[10] = _createElementVNode("label", {
                        for: "category",
                        class: "form-label fw-bold"
                      }, [
                        _createTextVNode(" Category "),
                        _createElementVNode("span", { class: "text-danger" }, "*")
                      ], -1)),
                      _withDirectives(_createElementVNode("select", {
                        id: "category",
                        class: _normalizeClass(["form-select", { 'is-invalid': errors.category }]),
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.category) = $event)),
                        required: ""
                      }, [
                        _cache[9] || (_cache[9] = _createElementVNode("option", {
                          value: "",
                          disabled: ""
                        }, "Select a category", -1)),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(kbCategories.value, (cat) => {
                          return (_openBlock(), _createElementBlock("option", {
                            key: cat.value,
                            value: cat.value
                          }, _toDisplayString(cat.label), 9, _hoisted_18))
                        }), 128))
                      ], 2), [
                        [_vModelSelect, formData.category]
                      ]),
                      (errors.category)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(errors.category), 1))
                        : _createCommentVNode("", true),
                      _cache[11] || (_cache[11] = _createElementVNode("small", { class: "form-text text-muted" }, " Categorize your knowledge base for better organization ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_20, [
                      _cache[12] || (_cache[12] = _createElementVNode("label", {
                        for: "description",
                        class: "form-label fw-bold"
                      }, " Description ", -1)),
                      _withDirectives(_createElementVNode("textarea", {
                        class: _normalizeClass(["form-control", { 'is-invalid': errors.description }]),
                        id: "description",
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.description) = $event)),
                        rows: "4",
                        placeholder: "Enter a description for this knowledge base"
                      }, null, 2), [
                        [_vModelText, formData.description]
                      ]),
                      (errors.description)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(errors.description), 1))
                        : _createCommentVNode("", true),
                      _cache[13] || (_cache[13] = _createElementVNode("small", { class: "form-text text-muted" }, " Optional: Describe the purpose and contents of this knowledge base ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", _hoisted_23, [
                        _withDirectives(_createElementVNode("input", {
                          class: "form-check-input ms-1",
                          type: "checkbox",
                          id: "isActive",
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.isActive) = $event))
                        }, null, 512), [
                          [_vModelCheckbox, formData.isActive]
                        ]),
                        _cache[14] || (_cache[14] = _createElementVNode("label", {
                          class: "form-check-label ps-2",
                          for: "isActive"
                        }, " Active ", -1))
                      ]),
                      _cache[15] || (_cache[15] = _createElementVNode("small", { class: "form-text text-muted" }, " Only active knowledge bases can be used for document retrieval ", -1))
                    ]),
                    (submitError.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                          _cache[16] || (_cache[16] = _createElementVNode("i", { class: "fa fa-exclamation-triangle me-2" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(submitError.value), 1)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_25, [
                      _createVNode(_component_router_link, {
                        to: "/admin/knowledge-bases",
                        class: "btn btn-outline-secondary"
                      }, {
                        default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                          _createElementVNode("i", { class: "fa fa-times me-1" }, null, -1),
                          _createTextVNode(" Cancel ", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("button", {
                        type: "submit",
                        class: "btn btn-primary",
                        disabled: isSaving.value
                      }, [
                        (isSaving.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_27, [...(_cache[18] || (_cache[18] = [
                              _createElementVNode("span", {
                                class: "spinner-border spinner-border-sm me-1",
                                role: "status"
                              }, null, -1),
                              _createTextVNode(" Saving... ", -1)
                            ]))]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_28, [
                              _cache[19] || (_cache[19] = _createElementVNode("i", { class: "fa fa-save me-1" }, null, -1)),
                              _createTextVNode(" " + _toDisplayString(isEditMode.value ? "Update" : "Create"), 1)
                            ]))
                      ], 8, _hoisted_26)
                    ])
                  ], 32))
            ])
          ])
        ])
      ])
    ])
  ]))
}
}

})